import api from './api';

/**
 * P5b — the STAFF side of label scanning (design §4–§5).
 *
 * Everything here is venue-scoped and provisional: staff correct the
 * READING and commit a draft item that works immediately. Nothing here can
 * write canonical identity, and there is no paid probe on this surface —
 * those are the operator's, deliberately.
 */

export interface StaffScanImage { side: 'front' | 'back'; url?: string; sha256?: string }

export interface StaffScanCandidate {
  identityRef: { wineNameId?: string; producerId?: string };
  display: string;
  confidence: { overall: number };
  matchDetail: Record<string, string>;
  disambiguation?: { fullName?: string | null; country?: string | null; provisional?: boolean };
}

export interface StaffScan {
  _id: string;
  status: string;
  createdAt: string;
  images: StaffScanImage[];
  visionRead?: Record<string, { text?: string | null; value?: number | null; confidence?: number }>;
  resolution?: { verdict?: string; candidates?: StaffScanCandidate[]; flags?: Array<{ code: string; userMessage?: string; detail: string }> };
  corrections?: Array<Record<string, unknown>>;
  staffAcceptance?: { inventoryItemId: string; converged: boolean };
}

const TERMINAL = ['matched', 'matched_producer', 'proposed', 'flagged', 'failed'];
export const isTerminal = (status: string): boolean => TERMINAL.includes(status);

const staffScanService = {
  /**
   * venueId '' uploads on the ADMIN path — the wine database has no venue,
   * and its scans are filed under the admin marker server-side. One
   * pipeline either way; only the owner differs.
   */
  upload: async (venueId: string, front: File, back?: File | null): Promise<StaffScan> => {
    const form = new FormData();
    form.append('front', front);
    if (back) form.append('back', back);
    const url = venueId ? `/admin/venue/${venueId}/label-scan` : '/admin/label-scan';
    const res = await api.post(url, form, { headers: { 'Content-Type': 'multipart/form-data' } });
    return res.data;
  },

  get: async (scanId: string): Promise<StaffScan> => {
    const res = await api.get(`/admin/label-scan/${scanId}`);
    return res.data;
  },

  /** A correction informs the operator; it never touches the wine database. */
  addCorrection: async (venueId: string, scanId: string, body: {
    slot: string; from?: string | number | null; to?: string | number | null;
    kind: 'edit' | 'pick_candidate' | 'vintage' | 'photo_added';
  }): Promise<void> => {
    await api.post(`/admin/venue/${venueId}/label-scan/${scanId}/corrections`, body);
  },

  /**
   * Accept: converge on a candidate, or draft a provisional identity.
   * `createItem: false` is the ENTRY-METHOD shape — resolve the wine and
   * hand it back so the host flow (which owns quantity, size, price)
   * creates the item itself.
   */
  accept: async (venueId: string, scanId: string, body: {
    wineNameId?: string;
    fields?: { producerName?: string; regionName?: string; specialName?: string; vintage?: number | null; grapes?: string[] };
    sizeValue?: number; sizeUnit?: string; createItem?: boolean;
  }): Promise<{
    accepted: boolean; converged: boolean; provisional: boolean; item: any; message: string;
    wineId: string; wineNameId: string;
    wine: { _id: string; name: string; displayVintage: string; vintageType: string };
  }> => {
    const res = await api.post(`/admin/venue/${venueId}/label-scan/${scanId}/accept`, body);
    return res.data;
  },
};

export default staffScanService;
